import React, { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  KPI_GAP_PX,
  kpiGridColumns,
  kpiSpanLast,
} from "./salesDashboardConfig";

// Short rows (≤ 3 tiles) stop stretching here so a value never floats in a
// half-empty card on wide screens.
const MAX_TILE_WIDTH_SHORT_ROW = 360;

/**
 * KPI tile grid with balanced rows that follow the live container width
 * (ResizeObserver), so 2–8 tiles lay out evenly at wide, laptop and phone
 * widths. Children are the tiles, in order.
 */
const KpiGrid = ({ children, mb = 2.5 }) => {
  const tiles = React.Children.toArray(children).filter(Boolean);
  const count = tiles.length;
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect?.width;
      if (next != null) setWidth(next);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const columns = kpiGridColumns(count, width);
  const shortRow = count <= 3 && columns === count;
  const spanLast = kpiSpanLast(count, columns);

  return (
    <Box
      ref={ref}
      data-testid="kpi-grid"
      data-columns={columns}
      sx={{
        display: "grid",
        gridTemplateColumns: shortRow
          ? `repeat(${columns}, minmax(0, ${MAX_TILE_WIDTH_SHORT_ROW}px))`
          : `repeat(${columns}, minmax(0, 1fr))`,
        gap: `${KPI_GAP_PX}px`,
        mb,
      }}
    >
      {tiles.map((tile, index) => (
        <Box
          key={tile.key ?? index}
          data-testid="kpi-grid-cell"
          sx={
            spanLast && index === count - 1
              ? { gridColumn: "1 / -1", minWidth: 0 }
              : { minWidth: 0 }
          }
        >
          {tile}
        </Box>
      ))}
    </Box>
  );
};

export default KpiGrid;
